/**
 * `arch/entityDetails` — everything an inspector shows about ONE entity.
 *
 * The peer of `arch/navTree`, and answered here for the same reason: the
 * server already holds a warm model AND the dependency graph per package, and
 * the relationship half of an inspector (who calls this, who does it call,
 * which processes it takes part in) is a graph query — a host computing that
 * itself would be re-deriving the whole edge set on every selection.
 *
 * Kept a separate request rather than fattened onto the nav tree: the tree is
 * re-sent on every model change, and per-entity detail for a whole workspace
 * on that hot path would dwarf the structure the host actually needs.
 *
 * The projection itself is `buildEntityDetails` in viewer-core: pure, shared,
 * and tested there. This file is only the protocol shell around it.
 */
import { buildEntityDetails, type EntityDetails } from "@archlang/viewer-core";
import type { PackageRouter } from "../workspace/package-router.js";

export interface EntityDetailsParams {
  /** Nav-tree address of the entity — `moduleDiffId` or `<moduleId>#<name>`. */
  readonly id: string;
  /** Package root to look in; omit to search every open package. */
  readonly root?: string;
}

export interface EntityDetailsResult {
  /** Null when no open package knows the id — mid-edit, renamed, or removed. */
  readonly details: EntityDetails | null;
}

export const ENTITY_DETAILS_REQUEST = "arch/entityDetails";

export function provideEntityDetails(
  routers: readonly PackageRouter[],
  params: EntityDetailsParams,
): EntityDetailsResult {
  if (!params?.id) return { details: null };
  const seek = (rootFilter: string | undefined): EntityDetails | null => {
    for (const router of routers) {
      for (const { root, services } of router.packages()) {
        // A host that knows which package it is looking at says so; one that
        // has only an id (a board selection, a stale panel) does not, and
        // searching is what lets the panel keep working across packages. Ids
        // are space-qualified, so a hit in the wrong package is not a real
        // risk.
        if (rootFilter && root.uri !== rootFilter) continue;
        const details = buildEntityDetails(
          // The STUBBED model, matching the nav tree: a process-first
          // package's modules are implied rather than declared, and selecting
          // one of those rows must not answer "not in the current model".
          services.workspace.stubbedModel(),
          services.workspace.dependencyGraph(),
          params.id,
          // "Mentioned in" — who points at this entity with `[[Name]]`. Built
          // structurally per assemble, so handing it over costs nothing here;
          // rebuilding it per selection would scan every description and `.md`
          // body in the package on the panel's hot path.
          { backlinks: services.workspace.backlinkIndex() },
        );
        if (details) return details;
      }
    }
    return null;
  };
  // A root NO package answers to (the host derived it from a file path, or
  // encoded it differently than discovery did) must degrade to the search,
  // not turn a live entity into "not in the current model". A root that IS a
  // known package stays strict — an id from another package answers null.
  const rootKnown =
    !params.root ||
    routers.some((router) =>
      router.packages().some((p) => p.root.uri === params.root),
    );
  const details = seek(params.root) ?? (rootKnown ? null : seek(undefined));
  return { details };
}
